import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { BetTableBaccarat } from "../../casinoComponents/betTableBaccarat";
import VideoBox from "../../casinoComponents/videoBox";
import ResultBaccarat from "../resultui/resultBaccarat/ResultBaccarat";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { displayNetOdds } from "../../../utils/casinoNetOdds";
import "../../../styles/games/baccarat.css";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import { getBaccaratRulesHtml } from "../../../data/baccaratRulesHtml";
import BaccaratRules from "../../casinoComponents/videoBox/overlays/BaccaratRules";


// Reference game titles (lastresults2 g.cname) per route
const TITLES = { baccarat: "Baccarat", baccarat2: "Baccarat 2", pbaccarat: "Premium Baccarat", tbaccarat: "Tembo Baccarat" };

/** Baccarat, Baccarat 2 and Premium Baccarat (routes casino/baccarat, casino/baccarat2, casino/pbaccarat). */
export default function Baccarat() {
  const [gameData, setGameData] = useState(null);
  const [subData, setSubData] = useState([]);
  const [iframeSrc, setIframeSrc] = useState("");
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "baccarat";
  // Rules (video (i) icon): VideoBox's shared rules map has pbaccarat only; baccarat / baccarat2 use our own copy
  const ownRulesHtml = getCasinoRulesHtml(gameName) ? null : getBaccaratRulesHtml(gameName);
  const handleRulesClick = () => {
    setShowRules(true);
    if (IS_REF_MOBILE) setShowRulesModal(true);
  };

  useEffect(() => {
    const name = extractCasinoGame(pathname) || "baccarat";
    setIframeSrc(`https://stream-s-43.uhdmovies.online/casino-stream?id=${name}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
        setSubData(response?.data?.data?.t2 ?? response?.data?.data?.sub ?? []);
      } catch (err) {
        console.error("Error fetching game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId =
        gameData?.data?.data?.t1?.gmid?.toString() ||
        gameData?.data?.data?.mid?.toString() ||
        gameData?.data?.mid?.toString() ||
        gameData?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [
    gameData?.data?.data?.t1?.gmid,
    gameData?.data?.data?.mid,
    gameData?.data?.mid,
    gameData?.mid,
  ]);

  const fetchMyBetsData = useCallback(async () => {
    const name = extractCasinoGame(pathname) || "baccarat";
    if (!name) return;
    try {
      const response = await getCasinoOpenBets(name);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          // the board's "to 1" price (stored odds are decimal)
          odds: displayNetOdds(name, bet.odds ?? bet.userrate ?? "0"),
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  useEffect(() => {
    const matchId =
      gameData?.data?.data?.t1?.gmid?.toString() ||
      gameData?.data?.data?.mid?.toString() ||
      gameData?.data?.mid?.toString() ||
      gameData?.mid?.toString();

    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, gameData]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const resultData =
    gameData?.lrs ?? gameData?.data?.data?.lrs ?? gameData?.data?.lrs ?? [];

  const roundId =
    gameData?.data?.data?.t1?.gmid ||
    gameData?.data?.data?.mid ||
    gameData?.data?.mid ||
    gameData?.mid ||
    "";

  // Reference: casino-detail (bet table + Last Result) only once the market list has loaded
  const cards = String(gameData?.data?.data?.t1?.card || gameData?.data?.data?.card || "").split(",");

  return (
    <CasinoTableLayout
      tableClass="baccarat"
      rightSide={
        <MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />
      }
    >
      <>
        {/* Reference markup: .game-heading > span.card-header-title + span#round-id */}
        <div className="game-heading">
          <span className="card-header-title">{TITLES[gameName] || "Baccarat"}</span>{" "}
          <span id="round-id">ROUND ID: {roundId}</span>
        </div>{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={gameData?.data?.data?.lt}
          gameType={gameName}
          resultData={gameData?.data?.data?.t1?.card || gameData?.data?.data?.card}
          onRulesClick={ownRulesHtml ? handleRulesClick : undefined}
          cardsOverlay={
            ownRulesHtml ? (
              <BaccaratRules
                html={ownRulesHtml}
                show={showRules}
                onClose={() => setShowRules(false)}
                showModal={showRulesModal}
                onCloseModal={() => setShowRulesModal(false)}
              />
            ) : null
          }
        />{" "}
        {subData.length > 0 && (
          <div className="casino-detail">
            <BetTableBaccarat
              data={subData}
              cards={cards}
              gameType={gameName}
              onBetClick={handleBetClick}
              exposures={exposures}
              myBets={myBets}
            />{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              resultComponent={<ResultBaccarat gameType={gameName} data={resultData} />}
            />
          </div>
        )}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
